import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LocationDataService from '../../services/location.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const LocationList = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const response = await LocationDataService.getAll();
      setLocations(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      showSnackbar(t('locations.errorLoadingLocations') + ': ' + (error.response?.data?.message || error.message), 'error');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await LocationDataService.delete(deleteDialog.id);
      showSnackbar(t('locations.deletedLocation'));
      fetchLocations();
    } catch (error) {
      showSnackbar(t('locations.errorDeletingLocation'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (location.address || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('locations.title')}</Typography>
            {hasPermission('locations.new') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/locations/new')}
              >
                {t('locations.newLocation')}
              </Button>
            )}
          </Box>

          <TextField
            placeholder={t('locations.searchLocations')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('locations.name')}</TableCell>
                  <TableCell>{t('locations.address')}</TableCell>
                  <TableCell>{t('locations.phone')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell align="center">{t('locations.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredLocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t('locations.noLocationsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLocations.map((location) => (
                    <TableRow key={location.id} hover>
                      <TableCell>{location.name}</TableCell>
                      <TableCell>{location.address || '—'}</TableCell>
                      <TableCell>{location.phone || '—'}</TableCell>
                      <TableCell>{location.active === false ? t('common.inactive') : t('common.active')}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('locations.edit') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/locations/${location.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission('locations.delete') && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(location.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('locations.deleteLocation')}</DialogTitle>
        <DialogContent>
          <Typography>{t('locations.deleteConfirmation')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationList;
